import {useEffect, useRef} from 'react';
import {destroy, IAnyType, isAlive} from 'mobx-state-tree';
import {useRootStore} from '@joyrideautos/ui-models/src/stores/rootStore/useRootStore';
import {action, makeObservable, observable, when} from 'mobx';
import {useRouter} from '../router/Router';
import makeLogger, {LoggerStatusEnum} from '@joyrideautos/ui-logger/src/Logger';

const logger = makeLogger(undefined)('usePresenter', LoggerStatusEnum.DISABLED);
const createPresenter =
    <VMT extends IAnyType>(
        presenter: VMT,
        extraDeps?: (rootStore: any, routerParams: any) => {snapshot?: VMT['CreationType']; env?: any}
    ) =>
    (rootStore: any, routerParams: any): VMT['Type'] => {
        const {snapshot, env} = (extraDeps && extraDeps(rootStore, routerParams)) ?? {};
        logger.log(`createPresenter ${presenter.name}`, {snapshot, env});
        return presenter.create(snapshot, {rootStore, routerParams, ...env});
    };

class PresenterHolder<T> {
    presenter: T | null = null;

    constructor() {
        makeObservable(this, {
            presenter: observable,
            setPresenter: action,
        });
    }

    setPresenter(vm: T | null) {
        this.presenter = vm;
    }

    clearPresenter() {
        const presenter = this.presenter;
        this.presenter = null;
        requestAnimationFrame(() => {
            destroy(presenter);
        });
    }
}

export const usePresenter = <VMT extends IAnyType>(
    presenterModel: VMT,
    extraDeps?: (rootStore: any, routerParams: any) => {snapshot: VMT['CreationType']; env?: unknown}
): {presenter?: VMT['Type']} => {
    const presenterHolderRef = useRef(new PresenterHolder<VMT['Type']>());
    const rootStore = useRootStore();
    const router = useRouter();

    useEffect(() => {
        const presenterHolder = presenterHolderRef.current;
        const {authUserStore} = rootStore;
        const disposers: (() => void)[] = [];

        disposers.push(
            when(
                () => Boolean(authUserStore.userInfo),
                () => {
                    const {params, location, match} = router;
                    const presenter = createPresenter(presenterModel, extraDeps)(rootStore, {
                        router,
                        location,
                        ...params,
                        path: match.path,
                    });
                    presenterHolder.setPresenter(presenter);

                    if ('viewDidMount' in (presenter as any)) {
                        presenter.viewDidMount();
                    }

                    disposers.push(
                        when(
                            () => Boolean(authUserStore.loadingStatus.isReady),
                            () => {
                                if (isAlive(presenter) && 'userDataDidGet' in (presenter as any)) {
                                    presenter.userDataDidGet(authUserStore.isLoggedInUserAnonymous!);
                                }
                            }
                        )
                    );
                }
            )
        );
        return () => {
            const presenter = presenterHolder.presenter;
            if (presenter && isAlive(presenter) && 'viewWillUnmount' in (presenter as any)) {
                presenter.viewWillUnmount();
            }
            presenterHolder.clearPresenter();
            disposers.forEach((d) => d());
            logger.log(`[usePresenter] destroy ${presenterModel.name}`);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return presenterHolderRef.current;
};
